// §4.1 — the kernel-side implementation of `HangarAPI`, shared by both transports.
//
// This module was assembled in stages, one milestone at a time, and each group below names the
// task that filled it in. Every group in `HangarApiHost` is wired as of M10.1; the one part of
// §4.1 still outstanding is `exec?`, which is optional on the port and belongs to M13.2 — an app
// asking for it gets `undefined` and can say so, which is the honest answer for a capability the
// desktop does not have rather than a method that pretends and fails.
import { HangarApiError, type HangarEnv, type Payload, type Win } from '@hangar/shared';
import type { WmStore } from '../wm/store';
import type { SettingsService } from '../settings';
import type { ShellUiService } from '../shellui';
import type { AppLaunchService } from '../apps/launch';
import type { FileTypeRegistry } from '../apps/filetypes';
import type { SessionService } from '../wm/session';
import type { MenuService } from '../apps/menus';
import type { VfsGateway } from '../vfs/gateway';
import type { ClipboardService } from '../transfer/clipboard';
import type { NotifyService } from '../notify';
import { appSuppliedIconMarkup } from '../wm/icon';
import type { AppInstanceContext, HangarApiHost } from './port';

export interface ApiHostDeps {
  wm: WmStore;
  settings: SettingsService;
  shellui: ShellUiService;
  launch: AppLaunchService;
  /** §9.2 — which app opens a path, for `launch.openWith`. */
  filetypes: FileTypeRegistry;
  /** §5.6 — where an app's session blob is kept between boots. */
  session: SessionService;
  /** §9.3 `menu` — where a window's published tree is kept, for whichever skin draws it. */
  menus: MenuService;
  /** §9.5 — the only route from an app to the filesystem. */
  vfs: VfsGateway;
  /** §10.1 — the ring, and the gesture gate on reads. */
  clipboard: ClipboardService;
  /** §13 — the Center, the toasts, and the do-not-disturb rule. */
  notify: NotifyService;
  /**
   * §13 — where an in-process app's `onAction` handler is kept. Owned by the kernel for the same
   * reason `onReceive` and `onCloseRequested` are: an action has to reach both transports, and one
   * register is what keeps them the same.
   */
  onNotifyAction: (instanceKey: string, cb: (id: string, action: string) => void) => void;
  /**
   * §10.2 / §10.3 — where an in-process app's `onReceive` handler is kept. Owned by the kernel for
   * the same reason `onCloseRequested` is (D-119): delivery has to reach both transports, and one
   * register is what keeps them the same.
   */
  onReceive: (instanceKey: string, cb: (payload: Payload, token: string) => void) => void;
  /**
   * §10.3 — the whole Share transaction: pick, launch-or-focus, deliver. It is the kernel's
   * because it spans `shellui`, `launch`, `wm` and the transfer tokens, and `api/host.ts` is a
   * translation layer rather than a place where a four-service dance belongs.
   */
  share: (ctx: AppInstanceContext, payload: Payload) => Promise<'delivered' | 'cancelled'>;
  /**
   * §4.1 `launch.app(appId, arg)` — hand the launched window its argument once it can hear one.
   *
   * The same waiting delivery Share uses, and for the same reason: a window that was created one
   * line ago has not mounted, so a synchronous send would fire into nothing. Separate from `share`
   * because there is no picker and no choice — the caller already named the app (D-540).
   */
  deliverArg: (instanceKey: string, payload: Payload) => Promise<boolean>;
  /**
   * §18 F9 — where an in-process app's close veto is kept. Owned by the kernel rather than by this
   * module, because the thing that has to *ask* it is `kernel.requestClose`, which also has to ask
   * the bridge — and one register for both transports is what keeps the two answers the same.
   */
  onCloseRequested: (instanceKey: string, cb: () => Promise<'allow' | 'deny'>) => void;
  /** Resolved theme, already collapsed from the `system` preference. */
  theme: () => 'light' | 'dark';
  user: () => HangarEnv['user'];
  locale?: () => string;
}

export function createApiHost(deps: ApiHostDeps): HangarApiHost {
  const winOf = (ctx: AppInstanceContext): Win => {
    const win = deps.wm.get(ctx.winId);
    if (!win) throw new HangarApiError('NOT_FOUND', `window ${ctx.winId} is gone`);
    return win;
  };

  /** Callbacks an app registered, keyed by window instance. Cleared when the window closes. */
  const commandHandlers = new Map<string, (id: string) => void>();

  return {
    env(ctx) {
      return {
        appId: ctx.appId,
        skin: deps.settings.get('skin'),
        theme: deps.theme(),
        locale: deps.locale?.() ?? 'en-US',
        user: deps.user(),
      };
    },

    // ── fs — the §9.5-scoped gateway, and nothing else (M4.8) ──────────────────────────────
    fs: {
      list: (ctx, dir) => deps.vfs.list(ctx, dir),
      read: (ctx, path) => deps.vfs.read(ctx, path),
      readText: (ctx, path) => deps.vfs.readText(ctx, path),
      write: (ctx, path, data) => deps.vfs.write(ctx, path, data),
      mkdir: (ctx, path) => deps.vfs.mkdir(ctx, path),
      move: (ctx, from, to) => deps.vfs.move(ctx, from, to),
      remove: (ctx, path) => deps.vfs.remove(ctx, path),
      versions: (ctx, path) => deps.vfs.versions(ctx, path),
      restore: (ctx, path, versionId) => deps.vfs.restore(ctx, path, versionId),
      watch: (ctx, dir, cb) => deps.vfs.watch(ctx, dir, cb),
      // Shell-drawn, and the gesture is the grant (§9.5, §19.4).
      pickOpen: (ctx, opts) => deps.vfs.pickOpen(ctx, opts),
      pickSave: (ctx, opts) => deps.vfs.pickSave(ctx, opts),
    },

    window: {
      setTitle(ctx, title) {
        deps.wm.setChrome(winOf(ctx).id, { title });
      },
      setIcon(ctx, svg) {
        // §9.4 — wrapped, never injected. An icon that is not usable leaves the window with the
        // one it had, which is better than a blank square (D-122).
        const iconSvg = appSuppliedIconMarkup(svg);
        if (iconSvg === null) return;
        deps.wm.setChrome(winOf(ctx).id, { iconSvg });
      },
      setDirty(ctx, dirty) {
        deps.wm.setChrome(winOf(ctx).id, { dirty });
      },
      setBusy(ctx, busy) {
        deps.wm.setChrome(winOf(ctx).id, { busy });
      },
      onCloseRequested(ctx, cb) {
        deps.onCloseRequested(ctx.instanceKey, cb);
      },
      /**
       * §4.1 — a second window of the *same* app.
       *
       * `width`, `height` and `arg` are honoured as of M58.3 (D-540). All three were declared in
       * `OpenWindowOpts` and dropped on the floor here, which is the same defect `launch.app` had:
       * §34.4's pinned note is a 280×240 window that has to be told which note it shows, and both
       * halves of that were already in the type.
       */
      async open(ctx, opts) {
        const id = deps.launch.launch(ctx.appId, {
          forceNew: true,
          ...(opts?.title !== undefined ? { title: opts.title } : {}),
          ...(opts?.width !== undefined && opts.height !== undefined
            ? { size: { w: opts.width, h: opts.height } }
            : {}),
        });
        if (!id) throw new HangarApiError('NOT_FOUND', `app ${ctx.appId} is not installed`);
        if (opts?.arg) {
          const instanceKey = deps.wm.get(id)?.instanceKey;
          if (instanceKey !== undefined) await deps.deliverArg(instanceKey, opts.arg);
        }
        return id;
      },
      close(ctx) {
        deps.wm.close(winOf(ctx).id);
      },
    },

    // ── menu / command (M8.9) ──────────────────────────────────────────────────────────────
    //
    // Publishing is per *window instance*: two windows of the same app can have different things
    // greyed out. Delivery of `command/fire` is the kernel's (`fireCommand`), because which
    // transport an instance lives behind is not this module's business.
    menu: {
      publish(ctx, tree) {
        // `winOf` first: publishing for a window that has gone should say so, not accumulate a
        // tree nobody will ever clear.
        deps.menus.publish(winOf(ctx).instanceKey, tree);
      },
      onCommand(ctx, cb) {
        commandHandlers.set(ctx.instanceKey, cb);
      },
    },

    // ── notify — §13 (M10.1) ──────────────────────────────────────────────────────────────
    //
    // The *window* is recorded, not just the app: §13's "clicking the body focuses the window that
    // sent it" has no answer otherwise for an app with more than one open.
    notify: {
      post(ctx, input) {
        return deps.notify.post(
          input,
          { appId: ctx.appId, instanceKey: ctx.instanceKey },
          Date.now(),
        );
      },
      onAction(ctx, cb) {
        deps.onNotifyAction(ctx.instanceKey, cb);
      },
    },

    // ── transfer — §10 ────────────────────────────────────────────────────────────────────
    //
    // §10.1's asymmetry, verbatim: a write is free, a read needs a gesture the *shell* saw. An
    // app cannot manufacture one, because the thing that arms a window is a real `paste` on the
    // shell's document or the user picking from the shell's own history panel.
    transfer: {
      copy(ctx, payload) {
        deps.clipboard.copy(payload, ctx.appId, Date.now());
      },
      readClipboard(ctx) {
        return deps.clipboard.read(ctx.instanceKey);
      },
      /**
       * §10.3 — the shell draws a picker of apps whose `accepts` matches, launches or focuses the
       * one chosen, and delivers. **The sender learns nothing else**: not who was offered, not
       * whether the target was already running — only `'delivered'` or `'cancelled'`.
       */
      share(ctx, payload) {
        return deps.share(ctx, payload);
      },
      onReceive(ctx, cb) {
        deps.onReceive(ctx.instanceKey, cb);
      },
    },

    dialog: {
      alert(ctx, message) {
        return deps.shellui.alert(message, ctx.appId);
      },
      confirm(ctx, message) {
        return deps.shellui.confirm(message, ctx.appId);
      },
      prompt(ctx, message, def) {
        return deps.shellui.prompt(message, def, ctx.appId);
      },
    },

    // §5.6 — an app's opaque blob, keyed by *window instance*, handed straight back on the
    // next boot. The shell never looks inside one.
    session: {
      save(ctx, blob) {
        deps.session.saveBlob(ctx.instanceKey, blob);
      },
      load(ctx) {
        return deps.session.loadBlob(ctx.instanceKey);
      },
    },

    launch: {
      /**
       * §4.1 — open or focus an app, and hand it `arg` if there is one.
       *
       * **`arg` was declared in §4.1 from M4 and never delivered** (D-540). Two Workbench features
       * need it — a markdown preview opening an `http(s)` link in the Browser (§34.3) and Explorer's
       * Compare opening Diff (§34.5) — and a second mechanism for "launch an app with something"
       * beside the one the type already promises would have been a worse answer than making the
       * promise true.
       *
       * It is a §10 delivery, not a §9.5 grant: the receiver gets *content* under a payload-scoped
       * token and no filesystem reach. That is what keeps this from becoming a way for one app to
       * hand another a file it may not read — the distinction §10.3 draws against "Open with".
       */
      async app(_ctx, appId, arg) {
        const winId = deps.launch.launch(appId);
        if (winId === null) {
          throw new HangarApiError('NOT_FOUND', `app ${appId} is not installed`);
        }
        if (!arg) return;
        const instanceKey = deps.wm.get(winId)?.instanceKey;
        // A window that vanished between the launch and here has nothing to be handed. Delivery
        // failure is silent for the same reason Share's is: the sender learns only what it needs.
        if (instanceKey !== undefined) await deps.deliverArg(instanceKey, arg);
      },

      /**
       * §9.2 — the registry picks the app, the shell opens the window, and the *window* is
       * granted the path (§9.5). The caller has to be able to read it first: otherwise an app
       * could make the shell put a file it may not touch on the user's screen.
       */
      openWith(ctx, path, appId) {
        if (!deps.vfs.canReach(ctx, path, 'read')) {
          throw new HangarApiError('FORBIDDEN', `${JSON.stringify(path)} is outside what ${ctx.appId} may reach`, {
            path,
          });
        }
        const target = appId ?? deps.filetypes.defaultFor(path);
        if (target === null || target === undefined) {
          throw new HangarApiError('NOT_FOUND', `no app is registered for ${JSON.stringify(path)}`, {
            path,
          });
        }
        const winId = deps.launch.launch(target, { openPath: path });
        if (winId === null) {
          throw new HangarApiError('NOT_FOUND', `app ${target} is not installed`, { path });
        }
        const opened = deps.wm.get(winId);
        // The gesture that chose the file is the grant, exactly as a picker's would be (§9.5).
        if (opened) deps.vfs.grant(opened.instanceKey, [path]);
      },

      openedPath(ctx) {
        return deps.launch.openedPath(ctx.instanceKey);
      },
    },
  };
}
